<script setup>
import { ref, computed, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { useAdminCenterStore } from '../../stores/AdminCenterStore'
import { useSortedBatch } from '../../composables/useSortedBatch'
import { sortEmployerRows, DEFAULT_EMPLOYER_SORT, EMPLOYER_SORT_KEYS } from '../../utils/adminCenterTableSort'
import { handleErrors } from '../../utils/errorHandler'
import BcsCard from '../common/BcsCard.vue'
import BcsStatusPill from '../common/BcsStatusPill.vue'
import BcsDropdownSelect from '../common/BcsDropdownSelect.vue'
import { getAccountOwners, normalizeOwnerEmail } from '../../utils/accountOwnersHelpers'

const MAX_ACCOUNT_OWNERS = 3

const emit = defineEmits(['showAlert'])

const adminCenterStore = useAdminCenterStore()
const {
  isLoadingEmployers,
  allEmployers,
  unassignedEmployersList,
  totalAllEmployers,
  totalUnassignedEmployers,
  allActiveUsers
} = storeToRefs(adminCenterStore)

const COLUMNS = [
  { id: 'status', label: 'Status', sortKey: EMPLOYER_SORT_KEYS.STATUS, width: '18%', thClass: 'ps-4' },
  { id: 'employer', label: 'Employer', sortKey: EMPLOYER_SORT_KEYS.NAME, width: '20%' },
  { id: 'planStart', label: 'Plan Year Start', sortKey: EMPLOYER_SORT_KEYS.PLAN_START, width: '16%' },
  { id: 'funding', label: 'Medical Plan Funding', sortKey: EMPLOYER_SORT_KEYS.FUNDING, width: '22%' },
  { id: 'reassign', label: 'Reassign Owner', sortKey: null, width: '24%' }
]

const allTable = reactive(useSortedBatch(allEmployers, sortEmployerRows, DEFAULT_EMPLOYER_SORT))
const unassignedTable = reactive(useSortedBatch(unassignedEmployersList, sortEmployerRows, DEFAULT_EMPLOYER_SORT))

const activeTab = ref('all')
const isSubmitting = ref(false)

// Track changes by employerId (not planYearId), since account owners live
// on the employer document. This ensures all plan year rows for the same
// employer stay in sync when the user changes the owners on any one of them.
const ownerChanges = ref({}) // { [employerId]: string[] }

const hasChanges = computed(() => Object.keys(ownerChanges.value).length > 0)

// Build owner options from active users for the dropdown.
// No 'Unassigned' sentinel in multi-select mode: an empty selection means unassigned.
const ownerOptions = computed(() =>
  allActiveUsers.value
    .map(u => ({ value: normalizeOwnerEmail(u.email), label: `${u.fullname}` }))
    .filter(o => o.value)
)

function formatDate(value) {
  if (!value) return 'N/a'
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'N/a'
  return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

function getSelectedOwners(employer) {
  if (ownerChanges.value[employer.employerId] !== undefined) {
    return ownerChanges.value[employer.employerId]
  }
  return getAccountOwners(employer)
}

const sameOwners = (a, b) => [...a].sort().join('|') === [...b].sort().join('|')

function onOwnersChange(employer, newOwners) {
  const owners = Array.isArray(newOwners) ? newOwners : []
  // If reverting to the original set, remove from changes
  if (sameOwners(owners, getAccountOwners(employer))) {
    const copy = { ...ownerChanges.value }
    delete copy[employer.employerId]
    ownerChanges.value = copy
  } else {
    ownerChanges.value = { ...ownerChanges.value, [employer.employerId]: owners }
  }
}

async function onRefresh() {
  ownerChanges.value = {}
  await adminCenterStore.refreshEmployers()
}

async function submitAssignments() {
  if (!hasChanges.value || isSubmitting.value) return
  isSubmitting.value = true

  try {
    const assignments = Object.entries(ownerChanges.value).map(([employerId, accountOwners]) => ({
      employerId,
      accountOwners
    }))

    if (assignments.length === 0) return

    await adminCenterStore.submitAssignments(assignments)
    ownerChanges.value = {}

    emit('showAlert', `Successfully updated ${assignments.length} employer assignment${assignments.length !== 1 ? 's' : ''}!`)
  } catch (error) {
    handleErrors('EmployerAssignments.submitAssignments', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <BcsCard title="Employer Assignments">
    <template #header-right>
      <button
        class="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
        type="button"
        id="employerAssignmentsRefresh"
        :disabled="isLoadingEmployers"
        @click="onRefresh"
      >
        <i class="bi bi-arrow-clockwise d-flex py-1" :class="{ 'spin-animation': isLoadingEmployers }"></i>
      </button>
    </template>
    <div class="px-3 pt-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs mb-0 border-0">
        <li class="nav-item">
          <button
            class="nav-link employer-tab text-16"
            :class="{ active: activeTab === 'all' }"
            @click="activeTab = 'all'"
            type="button"
          >
            All Employers ({{ totalAllEmployers }})
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link employer-tab text-16"
            :class="{ active: activeTab === 'unassigned' }"
            @click="activeTab = 'unassigned'"
            type="button"
          >
            Unassigned Employers ({{ totalUnassignedEmployers }})
          </button>
        </li>
      </ul>
    </div>

    <!-- All Employers Tab -->
    <div v-if="activeTab === 'all'">
      <div v-if="isLoadingEmployers" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <table v-else id="employerAssignmentsTable" class="table employer-table align-middle mb-0">
        <colgroup>
          <col v-for="column in COLUMNS" :key="column.id" :style="{ width: column.width }" />
        </colgroup>
        <thead>
          <tr class="text-16">
            <th
              v-for="column in COLUMNS"
              :key="column.id"
              class="fw-bold py-3"
              :class="column.thClass"
              :aria-sort="allTable.ariaSort(column)"
              :data-column="column.id"
            >
              <button
                v-if="column.sortKey"
                type="button"
                class="column-sort"
                @click="allTable.toggleSort(column.sortKey)"
              >
                <span>{{ column.label }}</span>
                <span class="column-sort__icon" :class="{ 'is-active': allTable.isSortedBy(column) }">
                  <i class="bi" :class="allTable.sortIconClass(column)"></i>
                </span>
              </button>
              <span v-else>{{ column.label }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employer in allTable.visibleRows" :key="employer.planYearId" class="text-14">
            <td class="ps-4">
              <BcsStatusPill
                :text="employer.isAssigned ? 'Assigned' : 'Unassigned'"
                :color="employer.isAssigned ? 'success' : 'danger'"
                :bold="false"
              />
            </td>
            <td>{{ employer.name }}</td>
            <td>{{ formatDate(employer.planStartDate) }}</td>
            <td>{{ employer.medicalPlanFunding || '--' }}</td>
            <td class="pe-4">
              <BcsDropdownSelect
                :modelValue="getSelectedOwners(employer)"
                @update:modelValue="onOwnersChange(employer, $event)"
                :options="ownerOptions"
                :multiSelect="true"
                :maxSelections="MAX_ACCOUNT_OWNERS"
                placeholder="Unassigned"
                variant="primary"
              />
            </td>
          </tr>
          <tr v-if="allTable.sortedRows.length === 0 && !isLoadingEmployers">
            <td colspan="5" class="text-center text-muted py-3">No employers found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Unassigned Employers Tab -->
    <div v-else-if="activeTab === 'unassigned'">
      <div v-if="isLoadingEmployers" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <table v-else id="employerAssignmentsTable" class="table employer-table align-middle mb-0">
        <colgroup>
          <col v-for="column in COLUMNS" :key="'unassigned-' + column.id" :style="{ width: column.width }" />
        </colgroup>
        <thead>
          <tr class="text-16">
            <th
              v-for="column in COLUMNS"
              :key="column.id"
              class="fw-bold py-3"
              :class="column.thClass"
              :aria-sort="unassignedTable.ariaSort(column)"
              :data-column="column.id"
            >
              <button
                v-if="column.sortKey"
                type="button"
                class="column-sort"
                @click="unassignedTable.toggleSort(column.sortKey)"
              >
                <span>{{ column.label }}</span>
                <span class="column-sort__icon" :class="{ 'is-active': unassignedTable.isSortedBy(column) }">
                  <i class="bi" :class="unassignedTable.sortIconClass(column)"></i>
                </span>
              </button>
              <span v-else>{{ column.label }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employer in unassignedTable.visibleRows" :key="employer.planYearId" class="text-14">
            <td class="ps-4">
              <BcsStatusPill text="Unassigned" color="danger" :bold="false"/>
            </td>
            <td>{{ employer.name }}</td>
            <td>{{ formatDate(employer.planStartDate) }}</td>
            <td>{{ employer.medicalPlanFunding || '--' }}</td>
            <td class="pe-4">
              <BcsDropdownSelect
                :modelValue="getSelectedOwners(employer)"
                @update:modelValue="onOwnersChange(employer, $event)"
                :options="ownerOptions"
                :multiSelect="true"
                :maxSelections="MAX_ACCOUNT_OWNERS"
                placeholder="Unassigned"
                variant="primary"
              />
            </td>
          </tr>
          <tr v-if="unassignedTable.sortedRows.length === 0 && !isLoadingEmployers">
            <td colspan="5" class="text-center text-muted py-3">No unassigned employers found.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </BcsCard>

  <!-- Load More & Submit (outside card) -->
  <div v-if="activeTab === 'all' && !isLoadingEmployers && allTable.sortedRows.length > 0" id="employerAssignmentsLoadMore" class="d-flex justify-content-between align-items-start mt-3">
    <div>
      <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
        You're viewing {{ allTable.visibleRows.length }} of {{ allTable.sortedRows.length }} employers
      </p>
      <button
        v-if="allTable.hasMore"
        id="employerAssignmentsLoadMoreButton"
        type="button"
        class="btn btn-info text-16 mt-2"
        @click="allTable.loadMore()"
      >
        <med>Load More</med>
        <i class="bi bi-chevron-double-down ms-2"></i>
      </button>
    </div>
    <button
      type="button"
      class="btn btn-primary px-4"
      :disabled="!hasChanges || isSubmitting"
      @click="submitAssignments"
    >
      Submit Assignments
      <span v-if="isSubmitting" class="spinner-border spinner-border-sm ms-2" role="status"></span>
    </button>
  </div>
  <div v-if="activeTab === 'unassigned' && !isLoadingEmployers && unassignedTable.sortedRows.length > 0" id="employerAssignmentsLoadMore" class="d-flex justify-content-between align-items-start mt-3">
    <div>
      <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
        You're viewing {{ unassignedTable.visibleRows.length }} of {{ unassignedTable.sortedRows.length }} unassigned employers
      </p>
      <button
        v-if="unassignedTable.hasMore"
        id="employerAssignmentsLoadMoreButton"
        type="button"
        class="btn btn-info text-16 mt-2"
        @click="unassignedTable.loadMore()"
      >
        <med>Load More</med>
        <i class="bi bi-chevron-double-down ms-2"></i>
      </button>
    </div>
    <button
      type="button"
      class="btn btn-primary px-4"
      :disabled="!hasChanges || isSubmitting"
      @click="submitAssignments"
    >
      Submit Assignments
      <span v-if="isSubmitting" class="spinner-border spinner-border-sm ms-2" role="status"></span>
    </button>
  </div>
</template>

<style scoped lang="scss">
:deep(.card-body) {
  padding: 0;
}

.employer-tab {
  color: var(--bs-primary) !important;
  font-weight: 500 !important;
  border: 1px solid var(--bs-border-color);
  border-bottom: none;
  background-color: transparent;

  &.active {
    color: var(--bs-secondary) !important;
    background-color: var(--bs-white);
    border-bottom-color: var(--bs-white);
  }

  &:hover:not(.active) {
    color: var(--bs-primary);
  }
}

.employer-table {
  border-collapse: collapse;

  th, td {
    border-left: none;
    border-right: none;
  }

  thead th {
    background-color: var(--bs-white);
    border-bottom: 2px solid var(--bs-border-color);
    border-top: 1px solid var(--bs-border-color);
  }

  tbody td {
    border-bottom: 1px solid var(--bs-border-color);
  }

  tbody tr:last-child td {
    border-bottom: 1px solid var(--bs-border-color);
  }
}

.column-sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--bs-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
}

.column-sort__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: var(--bs-primary);
  font-size: 14px;

  &.is-active {
    background-color: #f7f1fd;
  }
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
